import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Heart, Zap, Scissors, Home, Baby, AlertCircle, Dog } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function BreedDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [breed, setBreed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getBreed(slug)
      .then(r => setBreed(r.data))
      .catch(() => navigate('/dog-breeds'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-48 mb-6" />
      <div className="aspect-video bg-gray-200 rounded-3xl mb-6" />
      <div className="space-y-3">
        <div className="h-6 bg-gray-200 rounded w-2/3" />
        <div className="h-4 bg-gray-200 rounded w-full" />
      </div>
    </div>
  );
  if (!breed) return null;

  const compat = breed.compatibility || {};
  const traits = [
    { icon: Zap, label: 'Energy', value: breed.energy_level, color: '#F5A623' },
    { icon: Scissors, label: 'Grooming', value: breed.grooming_needs, color: '#4A90E2' },
    { icon: Heart, label: 'Life Span', value: breed.life_span, color: '#7ED321' },
    { icon: Dog, label: 'Weight', value: breed.weight, color: '#5C636A' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="breed-detail-page">
      <SEO
        title={`${breed.name} - Dog Breed Guide`}
        description={`Learn about the ${breed.name}: ${breed.overview?.slice(0, 150)}...`}
        keywords={`${breed.name}, ${breed.name} breed, ${breed.name} temperament, ${breed.name} for sale, dog breeds guide`}
        canonical={`/dog-breeds/${breed.slug}`}
      />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#5C636A] hover:text-[#1A1D20] mb-6 transition-colors" data-testid="breed-back-button">
        <ArrowLeft className="w-4 h-4" /> All Breeds
      </button>

      <div className="relative aspect-video rounded-3xl overflow-hidden mb-8 shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
        <img src={breed.image_url || 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800'} alt={breed.name} className="w-full h-full object-cover" />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-8">
          <Badge className="bg-white/20 text-white border-0 rounded-full text-xs font-medium backdrop-blur-sm mb-2 px-3 py-1">
            {breed.group}
          </Badge>
          <h1 className="font-['Nunito'] font-extrabold text-3xl sm:text-4xl text-white tracking-tight" data-testid="breed-name">
            {breed.name}
          </h1>
        </div>
      </div>

      {/* Traits Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {traits.map((t, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <t.icon className="w-5 h-5 mb-2" style={{ color: t.color }} />
            <p className="text-xs text-[#5C636A] font-medium">{t.label}</p>
            <p className="font-semibold text-[#1A1D20] text-sm mt-0.5">{t.value}</p>
          </div>
        ))}
      </div>

      {/* Overview */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6">
        <h2 className="font-['Nunito'] font-bold text-xl text-[#1A1D20] mb-3">Overview</h2>
        <p className="text-[#5C636A] leading-relaxed">{breed.overview}</p>
      </div>

      {/* Temperament */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6">
        <h2 className="font-['Nunito'] font-bold text-xl text-[#1A1D20] mb-3">Temperament</h2>
        <div className="flex flex-wrap gap-2">
          {breed.temperament.split(',').map((t, i) => (
            <Badge key={i} className="bg-blue-50 text-[#4A90E2] border-0 rounded-full text-sm font-medium px-3 py-1">
              {t.trim()}
            </Badge>
          ))}
        </div>
      </div>

      {/* Compatibility */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6">
        <h2 className="font-['Nunito'] font-bold text-xl text-[#1A1D20] mb-4">Compatibility</h2>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Good with Kids', value: compat.kids, icon: Baby },
            { label: 'Apartment Friendly', value: compat.apartments, icon: Home },
            { label: 'First-time Owners', value: compat.first_time_owners, icon: Heart },
            { label: 'Hypoallergenic', value: compat.hypoallergenic, icon: AlertCircle },
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${item.value ? 'bg-green-50' : 'bg-gray-50'}`}>
              <item.icon className={`w-5 h-5 ${item.value ? 'text-[#7ED321]' : 'text-[#5C636A]'}`} />
              <div>
                <p className="text-sm font-medium text-[#1A1D20]">{item.label}</p>
                <p className={`text-xs font-semibold ${item.value ? 'text-[#7ED321]' : 'text-[#5C636A]'}`}>
                  {item.value ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-8">
        <Button
          onClick={() => navigate(`/dogs-for-sale?breed=${encodeURIComponent(breed.name)}`)}
          className="rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-bold px-8 h-12 shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
          data-testid="find-breed-listings"
        >
          Find {breed.name} for Sale <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
